import { Button, Input } from '@recipes/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '@/features/shared/form-field';

/**
 * Follow-up questions the generator asked (spec §7.3): answering them re-submits the
 * request with `answers` and `previousGenerationId`. Skipping shows the draft as is.
 */
export function QuestionsForm({
  questions,
  submitting,
  onSubmit,
  onSkip,
}: {
  questions: string[];
  submitting?: boolean;
  onSubmit: (answers: Record<string, string>) => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const answered = questions.filter((q) => (answers[q] ?? '').trim().length > 0).length;

  return (
    <form
      noValidate
      className="space-y-4 rounded-lg border border-info/50 bg-info/5 p-4"
      aria-labelledby="ai-questions-title"
      data-testid="ai-questions"
      onSubmit={(e) => {
        e.preventDefault();
        const filled = Object.fromEntries(
          Object.entries(answers).filter(([, v]) => v.trim().length > 0),
        );
        onSubmit(filled);
      }}
    >
      <div>
        <h2 id="ai-questions-title" className="text-lg font-semibold">
          {t('ai.questions.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('ai.questions.description')}</p>
      </div>
      {questions.map((q, i) => (
        <FormField key={q} label={q}>
          <Input
            value={answers[q] ?? ''}
            maxLength={500}
            data-testid={`ai-question-${i}`}
            onChange={(e) => setAnswers((prev) => ({ ...prev, [q]: e.target.value }))}
          />
        </FormField>
      ))}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onSkip}>
          {t('ai.questions.skip')}
        </Button>
        <Button
          type="submit"
          loading={submitting}
          disabled={answered === 0}
          data-testid="ai-questions-submit"
        >
          {t('ai.questions.submit')}
        </Button>
      </div>
    </form>
  );
}
